import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import DuplicateTournamentDialog from "../components/DuplicateTournamentDialog";
import { API_BASE_URL, api, ApiError, type Tournament } from "../lib/api";
import { formatDate } from "../lib/dateFormat";
import { SPORT_LABELS, sportLabel } from "../lib/labels";
import { tournamentTypeLabel } from "../lib/playerLabels";

function formatTypeLabel(value: string): string {
  return tournamentTypeLabel(value);
}

function formatDateRange(tournament: Tournament): string {
  if (!tournament.start_date && !tournament.end_date) return "Sin fechas";
  return `${formatDate(tournament.start_date, "Sin fecha")} – ${formatDate(tournament.end_date, "Sin fecha")}`;
}

function tournamentMatchesSearch(tournament: Tournament, query: string): boolean {
  if (!query) return true;
  const haystack = [
    tournament.name,
    tournament.tournament_type,
    formatTypeLabel(tournament.tournament_type),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

function resolveTournamentLogoUrl(logoUrl: string | null | undefined): string | null {
  if (!logoUrl) return null;
  if (/^https?:\/\//i.test(logoUrl)) return logoUrl;
  const appBase = API_BASE_URL.replace(/\/api\/v2\/?$/, "");
  return `${appBase}${logoUrl.startsWith("/") ? logoUrl : `/${logoUrl}`}`;
}

function canRequestTournamentDelete(tournament: Tournament): boolean {
  return tournament.status === "draft" || (!tournament.start_date && !tournament.end_date);
}

function todayIso(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Fase del torneo para tarjetas, tiles y chips (rediseño 2026-10-08). Se
 * calcula con lo que ya trae la lista (status + fechas, día local):
 * - «finished» (Terminados): status `closed`, o un torneo no borrador cuya
 *   fecha de fin ya pasó.
 * - «draft» (Borradores): status `draft`.
 * - «live» (En curso): no borrador ni terminado y su fecha de inicio ya llegó
 *   (o status `active` sin fecha de inicio).
 * - «registration» (Inscripciones abiertas): publicado, aún no empieza y la
 *   fecha límite de inscripción no ha pasado (o no tiene).
 * - «upcoming» (Próximo): publicado, aún no empieza y ya cerró inscripciones.
 *   No tiene tile propio; aparece en «Todos».
 */
type Phase = "live" | "registration" | "upcoming" | "draft" | "finished";

function tournamentPhase(t: Tournament, today: string): Phase {
  if (t.status === "closed") return "finished";
  if (t.status === "draft") return "draft";
  if (t.end_date && t.end_date < today) return "finished";
  if (t.start_date ? t.start_date <= today : t.status === "active") return "live";
  if (!t.registration_deadline || t.registration_deadline >= today) return "registration";
  return "upcoming";
}

const PHASE_LABEL: Record<Phase, string> = {
  live: "En curso",
  registration: "Inscripciones abiertas",
  upcoming: "Próximo",
  draft: "Borrador",
  finished: "Terminado",
};

type QuickFilter = "all" | "live" | "draft" | "finished";

function daysUntil(dateIso: string, today: string): number {
  const a = new Date(`${today}T00:00:00`).getTime();
  const b = new Date(`${dateIso}T00:00:00`).getTime();
  return Math.round((b - a) / 86_400_000);
}

export default function AdminTournamentsListPage() {
  const { token } = useAuth();
  const { askConfirm } = useAbortableConfirm([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [nameQuery, setNameQuery] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [quick, setQuick] = useState<QuickFilter>("all");
  const [uploadingLogoId, setUploadingLogoId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [brokenLogoIds, setBrokenLogoIds] = useState<Set<number>>(() => new Set());
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  // «Duplicar» (Eduardo 2026-10-03): torneo del que se abre el diálogo.
  const [duplicating, setDuplicating] = useState<Tournament | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const today = todayIso();

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.tournaments.listMine(token);
      setTournaments(response.data.tournaments);
      setBrokenLogoIds(new Set());
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los torneos.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  // Menú «⋯»: Escape o clic fuera lo cierran.
  useEffect(() => {
    if (openMenuId === null) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpenMenuId(null);
    }
    function onPointer(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!target?.closest?.("[data-card-menu]")) setOpenMenuId(null);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [openMenuId]);

  const phases = useMemo(() => {
    const map = new Map<number, Phase>();
    tournaments.forEach((t) => map.set(t.id, tournamentPhase(t, today)));
    return map;
  }, [tournaments, today]);

  const tiles = useMemo(() => {
    const out = { live: 0, registration: 0, draft: 0, finished: 0 };
    phases.forEach((phase) => {
      if (phase in out) out[phase as keyof typeof out] += 1;
    });
    return out;
  }, [phases]);

  const filteredTournaments = useMemo(() => {
    const query = nameQuery.trim().toLowerCase();
    return tournaments.filter(
      (tournament) =>
        tournamentMatchesSearch(tournament, query) &&
        (!sportFilter || (tournament.sport ?? "tenis") === sportFilter) &&
        (quick === "all" || phases.get(tournament.id) === quick),
    );
  }, [nameQuery, sportFilter, quick, phases, tournaments]);

  const activeCards = filteredTournaments.filter((t) => phases.get(t.id) !== "finished");
  // Terminados: lista compacta abajo, el más reciente primero.
  const finishedRows = filteredTournaments
    .filter((t) => phases.get(t.id) === "finished")
    .sort((a, b) => (b.end_date ?? b.start_date ?? "").localeCompare(a.end_date ?? a.start_date ?? ""));

  function clearFilters() {
    setNameQuery("");
    setSportFilter("");
    setQuick("all");
  }

  function focusSearch() {
    searchInputRef.current?.focus();
    searchInputRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  async function handleLogoUpload(t: Tournament, file: File | null | undefined) {
    if (!token || !file) return;
    setError(null);
    setMessage(null);
    setUploadingLogoId(t.id);
    try {
      const response = await api.tournaments.uploadLogo(token, t.id, file);
      // La respuesta de subir el logo no trae los datos que solo da la lista
      // («Duplicar», número de admins, conteos): se conservan los de la tarjeta.
      setTournaments((prev) =>
        prev.map((row) =>
          row.id === t.id
            ? {
                ...row,
                ...response.data.tournament,
                can_duplicate: row.can_duplicate,
                duplicate_preview: row.duplicate_preview,
                admin_count: row.admin_count,
                registered: row.registered,
                matches_total: row.matches_total,
                matches_played: row.matches_played,
                pending_reports: row.pending_reports,
              }
            : row,
        ),
      );
      setBrokenLogoIds((prev) => {
        const next = new Set(prev);
        next.delete(t.id);
        return next;
      });
      setMessage(`Logo actualizado para "${t.name}".`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo cargar el logo.");
      } else {
        setError("Error de red al cargar el logo.");
      }
    } finally {
      setUploadingLogoId(null);
    }
  }

  async function handleDelete(t: Tournament) {
    if (!token || !canRequestTournamentDelete(t)) return;
    const noDates = !t.start_date && !t.end_date;
    const confirmed = await askConfirm(
      noDates
        ? `¿Eliminar el torneo "${t.name}"? No tiene fechas programadas y esta acción no se puede deshacer.`
        : `¿Eliminar el torneo "${t.name}"? Esta acción no se puede deshacer.`, { acceptLabel: "Sí, eliminar torneo", tone: "danger" });
    if (!confirmed) return;
    setError(null);
    setMessage(null);
    setDeletingId(t.id);
    try {
      await api.tournaments.remove(token, t.id);
      setTournaments((prev) => prev.filter((row) => row.id !== t.id));
      setMessage(`Torneo "${t.name}" eliminado.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo eliminar el torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setDeletingId(null);
    }
  }

  function renderMenu(t: Tournament, hasLogo: boolean) {
    const open = openMenuId === t.id;
    const close = () => setOpenMenuId(null);
    const inputId = `adm-tourn-logo-${t.id}`;
    return (
      <div className="adm-players__menu" data-card-menu>
        <button
          type="button"
          className="btn btn--ghost btn--compact adm-players__menu-trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Más acciones de ${t.name}`}
          onClick={() => setOpenMenuId(open ? null : t.id)}
        >
          ⋯
        </button>
        {/* El input vive fuera del menú para que el archivo llegue aunque el menú se cierre. */}
        <input
          id={inputId}
          className="adm-tourn__file"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          disabled={uploadingLogoId === t.id}
          onChange={(event) => {
            const file = event.target.files?.[0];
            void handleLogoUpload(t, file);
            event.currentTarget.value = "";
          }}
        />
        {open ? (
          <div className="adm-players__menu-list" role="menu">
            <label
              role="menuitem"
              tabIndex={0}
              htmlFor={inputId}
              className="adm-players__menu-item"
              onClick={() => setTimeout(close, 0)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  document.getElementById(inputId)?.click();
                  close();
                }
              }}
            >
              {uploadingLogoId === t.id ? "Cargando logo…" : hasLogo ? "Cambiar logo" : "Cargar logo"}
            </label>
            {t.can_duplicate ? (
              <button
                type="button"
                role="menuitem"
                className="adm-players__menu-item"
                aria-label={`Duplicar torneo ${t.name}`}
                onClick={() => {
                  close();
                  setDuplicating(t);
                }}
              >
                Duplicar torneo
              </button>
            ) : null}
            {t.can_delete !== false && canRequestTournamentDelete(t) ? (
              <button
                type="button"
                role="menuitem"
                className="adm-players__menu-item adm-players__menu-item--danger"
                aria-label={`Eliminar torneo ${t.name}`}
                disabled={deletingId === t.id}
                onClick={() => {
                  close();
                  void handleDelete(t);
                }}
              >
                {t.status === "draft" ? "Eliminar borrador" : "Eliminar torneo"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  function renderLogo(t: Tournament, logoUrl: string | null, small = false) {
    const initials = t.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("");
    return (
      <span className={`adm-tourn__logo${small ? " adm-tourn__logo--sm" : ""}`}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={`Logo de ${t.name}`}
            onError={() => setBrokenLogoIds((prev) => new Set(prev).add(t.id))}
          />
        ) : (
          <span aria-hidden="true">{initials || "T"}</span>
        )}
        {uploadingLogoId === t.id ? <em>Cargando…</em> : null}
      </span>
    );
  }

  function renderProgress(t: Tournament, phase: Phase) {
    if (phase === "live") {
      const total = t.matches_total ?? 0;
      if (total <= 0) return null;
      const played = t.matches_played ?? 0;
      const pct = Math.round((played / total) * 100);
      return (
        <div className="adm-tourn__progress">
          <div className="adm-tourn__progress-label">
            <span>Partidos jugados</span>
            <strong>{played} de {total}</strong>
          </div>
          <div className="adm-tourn__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={`Partidos jugados ${played} de ${total}`}>
            <i style={{ width: `${pct}%` }} />
          </div>
        </div>
      );
    }
    if (phase === "registration" && t.registration_deadline) {
      const days = daysUntil(t.registration_deadline, today);
      const text = days <= 0 ? "Cierre de inscripciones hoy" : days === 1 ? "Cierre de inscripciones mañana" : `Cierre de inscripciones en ${days} días`;
      // Barra: cuánto falta en una ventana de 30 días (más cerca = más llena).
      const pct = Math.max(5, Math.min(100, Math.round(((30 - Math.min(days, 30)) / 30) * 100)));
      return (
        <div className="adm-tourn__progress">
          <div className="adm-tourn__progress-label"><span>{text}</span></div>
          <div className="adm-tourn__bar adm-tourn__bar--lime" aria-hidden="true"><i style={{ width: `${pct}%` }} /></div>
        </div>
      );
    }
    // Borradores: la lista no trae el avance del asistente; no se inventa.
    return null;
  }

  return (
    <section className="adm-players adm-tourn">
      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">Tus torneos</span>
          <h1 className="adm-players__title">
            Torneos <span className="adm-players__count">{loading ? "…" : tournaments.length}</span>
          </h1>
        </div>
        <div className="adm-players__head-actions">
          <Link className="adm-players__btn adm-players__btn--primary" to="/admin/tournaments/new">
            + Nuevo torneo
          </Link>
        </div>
      </header>

      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      {message && <p className="form-message">{message}</p>}

      <div className="adm-tourn__tiles">
        <button type="button" className="adm-tourn__tile" onClick={() => setQuick("live")}>
          <small>En curso</small>
          <strong>{tiles.live}</strong>
        </button>
        <div className="adm-tourn__tile">
          <small>Inscripciones abiertas</small>
          <strong>{tiles.registration}</strong>
        </div>
        <button type="button" className="adm-tourn__tile" onClick={() => setQuick("draft")}>
          <small>Borradores</small>
          <strong>{tiles.draft}</strong>
        </button>
        <button type="button" className="adm-tourn__tile" onClick={() => setQuick("finished")}>
          <small>Terminados</small>
          <strong>{tiles.finished}</strong>
        </button>
      </div>

      <div className="adm-players__panel">
        <div className="adm-players__toolbar" role="search" aria-label="Buscar torneos">
          <label className="adm-players__search">
            <span aria-hidden="true">⌕</span>
            <input
              ref={searchInputRef}
              type="search"
              value={nameQuery}
              onChange={(event) => setNameQuery(event.target.value)}
              placeholder="Buscar por nombre o tipo de torneo"
              aria-label="Buscar por nombre o tipo de torneo"
            />
          </label>
          <select
            className="adm-tourn__select"
            value={sportFilter}
            onChange={(event) => setSportFilter(event.target.value)}
            aria-label="Filtrar por deporte"
          >
            <option value="">Todos los deportes</option>
            {Object.entries(SPORT_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="adm-players__chips" role="group" aria-label="Filtrar por estado">
          {(
            [
              ["all", "Todos"],
              ["live", "En curso"],
              ["draft", "Borradores"],
              ["finished", "Terminados"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`adm-players__filter${quick === key ? " is-active" : ""}`}
              aria-pressed={quick === key}
              onClick={() => setQuick(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="adm-players__panel">
          <p className="muted">Cargando…</p>
        </div>
      ) : tournaments.length === 0 ? (
        <div className="adm-players__panel">
          <h2>Aún no tienes torneos.</h2>
          <p className="muted">Crea el primero con “Nuevo torneo”.</p>
          <Link className="adm-players__btn adm-players__btn--primary" to="/admin/tournaments/new">
            + Nuevo torneo
          </Link>
        </div>
      ) : filteredTournaments.length === 0 ? (
        <div className="adm-players__panel">
          <h2>No encontramos torneos.</h2>
          <p className="muted">Ajusta la búsqueda o limpia los filtros para ver más resultados.</p>
          <button type="button" className="btn btn--ghost" onClick={clearFilters}>
            Limpiar filtros
          </button>
        </div>
      ) : (
        <>
          {activeCards.length > 0 ? (
            <div className="adm-tourn__cards">
              {activeCards.map((t) => {
                const phase = phases.get(t.id) ?? "draft";
                const logoUrl = brokenLogoIds.has(t.id) ? null : resolveTournamentLogoUrl(t.logo_url);
                const pending = t.pending_reports ?? 0;
                return (
                  <article key={t.id} className={`adm-tourn__card adm-tourn__card--${phase}`}>
                    <div className="adm-tourn__card-top">
                      {renderLogo(t, logoUrl)}
                      <div className="adm-tourn__card-title">
                        <h3>{t.name}</h3>
                        <p>
                          {sportLabel(t.sport)} · {formatTypeLabel(t.tournament_type)} · {formatDateRange(t)}
                          {typeof t.admin_count === "number" ? <> · <span className="adm-tourn__admins">{t.admin_count} admin(s)</span></> : null}
                        </p>
                      </div>
                    </div>
                    <div className="adm-tourn__chips">
                      <span className={`adm-tourn__chip adm-tourn__chip--${phase}`}>{PHASE_LABEL[phase]}</span>
                      {phase !== "draft" ? <span className="adm-tourn__chip">{t.registered ?? 0} inscritos</span> : null}
                      {pending > 0 ? (
                        <Link className="adm-tourn__chip adm-tourn__chip--warn" to={`/admin/tournaments/${t.id}/result-reports`}>
                          {pending === 1 ? "1 resultado por revisar" : `${pending} resultados por revisar`}
                        </Link>
                      ) : null}
                    </div>
                    {renderProgress(t, phase)}
                    <div className="adm-tourn__card-actions">
                      {phase === "draft" ? (
                        <Link className="adm-tourn__open adm-tourn__open--lime" to={`/admin/tournaments/${t.id}`}>
                          Seguir configurando
                        </Link>
                      ) : (
                        <Link className="adm-tourn__open" to={`/admin/tournaments/${t.id}`}>
                          Abrir torneo
                        </Link>
                      )}
                      {renderMenu(t, Boolean(logoUrl))}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}

          {finishedRows.length > 0 ? (
            <div className="adm-players__panel adm-tourn__finished">
              <span className="adm-players__eyebrow">Terminados</span>
              {finishedRows.map((t) => {
                const logoUrl = brokenLogoIds.has(t.id) ? null : resolveTournamentLogoUrl(t.logo_url);
                return (
                  <article key={t.id} className="adm-tourn__row">
                    {renderLogo(t, logoUrl, true)}
                    <Link className="adm-tourn__row-name" to={`/admin/tournaments/${t.id}`}>
                      <strong>{t.name}</strong>
                      <small>
                        {t.status === "closed" ? "Cerrado" : "Terminado"}
                        {t.end_date ? ` · ${formatDate(t.end_date)}` : ""}
                        {typeof t.admin_count === "number" ? <> · <span className="adm-tourn__admins">{t.admin_count} admin(s)</span></> : null}
                      </small>
                    </Link>
                    {renderMenu(t, Boolean(logoUrl))}
                  </article>
                );
              })}
            </div>
          ) : null}
        </>
      )}

      {duplicating && token ? (
        <DuplicateTournamentDialog
          token={token}
          tournament={duplicating}
          preview={duplicating.duplicate_preview}
          onClose={() => setDuplicating(null)}
        />
      ) : null}

      <div className="tournaments-floating-actions" aria-label="Acciones rápidas">
        <Link className="tournaments-floating-actions__create" to="/admin/tournaments/new">
          ＋ Crear torneo
        </Link>
        <button
          type="button"
          className="tournaments-floating-actions__search"
          onClick={focusSearch}
          aria-label="Buscar torneo"
          title="Buscar torneo"
        >
          ⌕
        </button>
      </div>
    </section>
  );
}
